import React, { useMemo } from "react";
import "./puzzleBoard2D.css";

// Tablero 13x13 visto desde arriba (Puzzles, Ver partidos, lobby En vivo).
// Mismo diseño que el diagrama de Aprender: anillos de color por piso, centro dorado con estrella
// y letras A-M / números 1-13 alrededor.
// `board` llega del servidor: { "F4": "bR" } = casilla → bloques de abajo arriba
// (b azul, r rojo, g verde, y amarillo; en mayúscula el bloque especial).
const COLS = "ABCDEFGHIJKLM".split("");
const N = 13;
const COLOR_BY_CODE = { b: "blue", r: "red", g: "green", y: "yellow" };

const floorOf = (ci, r) => {
  const d = Math.max(Math.abs(ci - 6), Math.abs(r - 7));
  if (d === 0) return 4;
  if (d <= 2) return 3;
  if (d <= 4) return 2;
  return 1;
};

// Anillo 1 (borde) a 7 (centro); cada piso tiene dos anillos de tono distinto.
const ringOf = (ci, r) => 7 - Math.max(Math.abs(ci - 6), Math.abs(r - 7));

const STAR_POINTS = Array.from({ length: 10 }, (_, i) => {
  const a = -Math.PI / 2 + (i * Math.PI) / 5;
  const rr = i % 2 === 0 ? 46 : 21;
  return `${(50 + rr * Math.cos(a)).toFixed(1)},${(50 + rr * Math.sin(a)).toFixed(1)}`;
}).join(" ");

const PuzzleBoard2D = ({ board, size = 170, className = "", label = "" }) => {
  const cells = useMemo(() => {
    const list = [];
    for (let r = N; r >= 1; r -= 1) {
      for (let ci = 0; ci < N; ci += 1) {
        const key = `${COLS[ci]}${r}`;
        const stack = String((board && board[key]) || "")
          .split("")
          .map((code) => ({
            color: COLOR_BY_CODE[code.toLowerCase()] || "blue",
            special: code !== code.toLowerCase(),
          }));
        list.push({ key, ci, r, stack });
      }
    }
    return list;
  }, [board]);

  return (
    <div
      className={`xb2d ${className}`.trim()}
      style={{ width: size, height: size }}
      role="img"
      aria-label={label || "Vista previa del puzzle desde arriba"}
    >
      <div className="xb2d-frame">
        {COLS.map((col, ci) => (
          <React.Fragment key={`f${col}`}>
            <span className="xb2d-lbl top" style={{ gridColumn: ci + 2, gridRow: 1 }}>{col}</span>
            <span className="xb2d-lbl bottom" style={{ gridColumn: ci + 2, gridRow: N + 2 }}>{col}</span>
          </React.Fragment>
        ))}
        {Array.from({ length: N }, (_, i) => N - i).map((r) => (
          <React.Fragment key={`r${r}`}>
            <span className="xb2d-lbl left" style={{ gridColumn: 1, gridRow: N - r + 2 }}>{r}</span>
            <span className="xb2d-lbl right" style={{ gridColumn: N + 2, gridRow: N - r + 2 }}>{r}</span>
          </React.Fragment>
        ))}
        <div className="xb2d-cells">
          {cells.map(({ key, ci, r, stack }) => {
            const n = stack.length;
            const stripe = 100 / (n || 1);
            const ring = ringOf(ci, r);
            return (
              <div
                key={key}
                className="xb2d-cell"
                data-floor={floorOf(ci, r)}
                data-ring={ring}
                style={{ gridColumn: ci + 1, gridRow: N - r + 1 }}
              >
                {ring === 7 ? (
                  <svg className="xb2d-star" viewBox="0 0 100 100" aria-hidden="true">
                    <polygon points={STAR_POINTS} />
                  </svg>
                ) : null}
                {stack.map((piece, index) => (
                  <div
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    className={`xb2d-pc ${piece.color}${piece.special ? " special" : ""}${n > 1 ? " small" : ""}`}
                    style={n > 1 ? { bottom: `${index * stripe}%`, height: `${stripe}%`, zIndex: index + 2 } : undefined}
                  >
                    {piece.special ? "★" : ""}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PuzzleBoard2D;
